import type { LeagueTabKey, NavKey } from '@league/shared/contract';
import { createContext, type ReactNode, useContext } from 'react';

/**
 * WHAT THIS PERSON'S SCREENS CONTAIN — the menu and the league tabs — as `/api/me` says (2026-10-16).
 *
 * The server decides (`apps/api/src/auth/capabilities.ts`); the browser only draws it, so there is no
 * role logic here to drift from the server's. Presentation only: hiding a tab is not what keeps an
 * instructor out of the roster — the repository layer is.
 *
 * The default is everything a director sees, so a screen rendered on its own (in a test, say) looks
 * the way it always has.
 */
export interface Capabilities {
  nav: NavKey[];
  leagueTabs: LeagueTabKey[];
}

export const DIRECTOR_CAPABILITIES: Capabilities = {
  nav: ['dashboard', 'leagues', 'players', 'kit', 'playbook', 'notes', 'instructors', 'settings'],
  leagueTabs: ['overview', 'roster', 'registrations', 'seating', 'scores', 'standings', 'emails', 'close-out', 'settings'],
};

const CapabilitiesContext = createContext<Capabilities>(DIRECTOR_CAPABILITIES);

export function CapabilitiesProvider({ value, children }: { value: Capabilities; children: ReactNode }) {
  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
}

export function useCapabilities(): Capabilities {
  return useContext(CapabilitiesContext);
}

/**
 * Whether the signed-in person runs the league's whole show, or only its nights. Read from what the
 * server sent: somebody without the Roster tab is somebody running nights for a director.
 */
export function useRunsOnlyNights(): boolean {
  return !useCapabilities().leagueTabs.includes('roster');
}
